import api from './api';
import {
    AccessItem,
    AccessStatus,
    ActivityNode,
    ActivityUpdate,
    BaselineSummary,
    ExecutionOverview,
    ProjectMember,
    Risk,
    StatusReport,
    Workstream,
    ExecutionTask,
    ImportPreview,
    ImportResult,
} from '../types/execution';

export type ActivityPayload = Partial<Pick<ActivityNode,
    'name' | 'parent_id' | 'order' | 'planned_start' | 'planned_end' | 'actual_start' | 'actual_end' |
    'progress' | 'weight' | 'status' | 'milestone_label' | 'responsible_ids' | 'risk_ids'>>;

export type RiskPayload = Partial<Omit<Risk, 'id' | 'project_id' | 'workstream_name' | 'activity_ids'>> & {
    new_target_date?: string;
};

export type MemberPayload = Partial<Omit<ProjectMember, 'id' | 'project_id' | 'access' | 'access_complete'>>;

class ExecutionService {
    async getOverview(projectId: number, date?: string): Promise<ExecutionOverview> {
        const response = await api.get(`/api/projects/${projectId}/execution`, { params: date ? { date } : {} });
        return response.data;
    }

    // Frentes
    async createWorkstream(projectId: number, data: Partial<Workstream> & { with_default_phases?: boolean }) {
        return (await api.post(`/api/projects/${projectId}/workstreams`, data)).data as Workstream;
    }

    async updateWorkstream(workstreamId: number, data: Partial<Workstream>) {
        return (await api.put(`/api/workstreams/${workstreamId}`, data)).data as Workstream;
    }

    async deleteWorkstream(workstreamId: number) {
        await api.delete(`/api/workstreams/${workstreamId}`);
    }

    // Atividades
    async createActivity(workstreamId: number, data: ActivityPayload) {
        return (await api.post(`/api/workstreams/${workstreamId}/activities`, data)).data;
    }

    async updateActivity(activityId: number, data: ActivityPayload) {
        return (await api.put(`/api/activities/${activityId}`, data)).data;
    }

    async deleteActivity(activityId: number) {
        await api.delete(`/api/activities/${activityId}`);
    }

    async listActivityUpdates(activityId: number): Promise<ActivityUpdate[]> {
        return (await api.get(`/api/activities/${activityId}/updates`)).data;
    }

    async saveActivityUpdate(activityId: number, data: Partial<ActivityUpdate>): Promise<ActivityUpdate> {
        return (await api.put(`/api/activities/${activityId}/updates`, data)).data;
    }

    // Linhas de base
    async createBaseline(workstreamId: number, data: { name: string; reason?: string }): Promise<BaselineSummary> {
        return (await api.post(`/api/workstreams/${workstreamId}/baselines`, data)).data;
    }

    async getBaselineSchedule(baselineId: number, date?: string): Promise<{ baseline: BaselineSummary; workstream: Workstream }> {
        return (await api.get(`/api/baselines/${baselineId}`, { params: date ? { date } : {} })).data;
    }

    async deleteBaseline(baselineId: number) {
        await api.delete(`/api/baselines/${baselineId}`);
    }

    // Riscos e incidências
    async createRisk(projectId: number, data: RiskPayload): Promise<Risk> {
        return (await api.post(`/api/projects/${projectId}/risks`, data)).data;
    }

    async updateRisk(riskId: number, data: RiskPayload): Promise<Risk> {
        return (await api.put(`/api/risks/${riskId}`, data)).data;
    }

    async deleteRisk(riskId: number) {
        await api.delete(`/api/risks/${riskId}`);
    }

    // Equipe e acessos
    async createAccessItem(projectId: number, name: string): Promise<AccessItem> {
        return (await api.post(`/api/projects/${projectId}/access-items`, { name })).data;
    }

    async updateAccessItem(itemId: number, data: Partial<AccessItem>): Promise<AccessItem> {
        return (await api.put(`/api/access-items/${itemId}`, data)).data;
    }

    async deleteAccessItem(itemId: number) {
        await api.delete(`/api/access-items/${itemId}`);
    }

    async createMember(projectId: number, data: MemberPayload): Promise<ProjectMember> {
        return (await api.post(`/api/projects/${projectId}/members`, data)).data;
    }

    async importMembersFromAllocations(projectId: number): Promise<ProjectMember[]> {
        return (await api.post(`/api/projects/${projectId}/members/import-allocations`)).data;
    }

    async updateMember(memberId: number, data: MemberPayload): Promise<ProjectMember> {
        return (await api.put(`/api/project-members/${memberId}`, data)).data;
    }

    async deleteMember(memberId: number) {
        await api.delete(`/api/project-members/${memberId}`);
    }

    async setMemberAccess(memberId: number, itemId: number, status: AccessStatus): Promise<ProjectMember> {
        return (await api.put(`/api/project-members/${memberId}/access/${itemId}`, { status })).data;
    }

    // Relatórios
    async listStatusReports(projectId: number): Promise<StatusReport[]> {
        return (await api.get(`/api/projects/${projectId}/status-reports`)).data;
    }

    async createStatusReport(projectId: number, data: { report_date: string; title?: string; baseline_ids?: number[] }): Promise<StatusReport> {
        return (await api.post(`/api/projects/${projectId}/status-reports`, data)).data;
    }

    async deleteStatusReport(reportId: number) {
        await api.delete(`/api/status-reports/${reportId}`);
    }

    async downloadStatusReportPptx(reportId: number, fileName: string) {
        const response = await api.get(`/api/status-reports/${reportId}/pptx`, {
            responseType: 'blob',
            transformResponse: [(data) => data],
        });
        const url = window.URL.createObjectURL(response.data as Blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }

    // Usuários que participam do projeto (owner das frentes)
    async listProjectPeople(projectId: number): Promise<{ id: number; name: string; role: string }[]> {
        return (await api.get(`/api/projects/${projectId}/people`)).data;
    }

    // ------------------------------------------------------- tarefas (Daily)
    async listTasks(projectId: number): Promise<ExecutionTask[]> {
        return (await api.get(`/api/projects/${projectId}/tasks`)).data;
    }

    async createTask(projectId: number, data: TaskPayload): Promise<ExecutionTask> {
        return (await api.post(`/api/projects/${projectId}/tasks`, data)).data;
    }

    async updateTask(taskId: number, data: TaskPayload): Promise<ExecutionTask> {
        return (await api.put(`/api/tasks/${taskId}`, data)).data;
    }

    async deleteTask(taskId: number) {
        await api.delete(`/api/tasks/${taskId}`);
    }

    // ------------------------------------------- importação da planilha
    async previewImport(projectId: number, file: File): Promise<ImportPreview> {
        const form = new FormData();
        form.append('file', file);
        return (await api.post(`/api/projects/${projectId}/execution/import?preview=1`, form,
            { headers: { 'Content-Type': 'multipart/form-data' } })).data;
    }

    async runImport(projectId: number, file: File, sheets: { title: string; name: string }[],
        mode: 'update' | 'replace', importTasks: boolean): Promise<ImportResult> {
        const form = new FormData();
        form.append('file', file);
        form.append('sheets', JSON.stringify(sheets));
        form.append('mode', mode);
        form.append('import_tasks', String(importTasks));
        return (await api.post(`/api/projects/${projectId}/execution/import`, form,
            { headers: { 'Content-Type': 'multipart/form-data' } })).data;
    }
}

export type TaskPayload = Partial<Pick<ExecutionTask,
    'title' | 'description' | 'status' | 'priority' | 'due_date' | 'workstream_id' | 'activity_id' | 'member_id' | 'assignee_name'>> & { user_id?: number | null };

export const executionService = new ExecutionService();
export default executionService;
